import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { Uuid } from 'src/apis/api-utilities';
import type { AuctionTileResponse } from 'src/apis/transport-auction/types';
import type { OptionalTransportAuctionServiceRootRequest } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { NotFound } from 'src/utils/errors';
import { isVerificationProblemWithoutField } from 'src/utils/is-verification-problem-without-field';

type HookOptions = {
  root: OptionalTransportAuctionServiceRootRequest;
  tileId: Uuid;
};

type ExecuteOptions = {
  url: string;
};

export function useAuctionTile({ root, tileId }: HookOptions) {
  const auctionTile = useAxios(
    (axios, baseConfig, { url }: ExecuteOptions) =>
      axios.request<AuctionTileResponse>({
        ...baseConfig,
        url,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (isServerError(error, 404)) {
          // either unknown or too old
          return error;
        }
        if (isVerificationProblemWithoutField(error)) {
          // e.g. no relevant auctions found in this auction tile
          return error;
        }
        throw error;
      },
    }
  );

  const auctionTileLink = root?.response?.data._links.auctionTile;

  useEffect(() => {
    if (!auctionTileLink) return;

    const url = urlTemplate.parse(auctionTileLink.href).expand({ tileId });
    auctionTile.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [auctionTileLink, tileId]);

  if (!auctionTileLink) throw new NotFound();

  return auctionTile;
}

export type AuctionTileRequest = ReturnType<typeof useAuctionTile>;
